import { useState, useCallback } from 'react';

type UserLocation = {
  lat: number;
  lng: number;
  source: 'geolocation' | 'zipcode' | 'manual';
};

type UserLocationResponse = {
  userLocation: UserLocation | null;
  isUserLocationLoading: boolean;
  userLocationError: string | null;
  permissionDenied: boolean;
  requestGeolocation: () => void;
  setManualLocation: (lat: number, lng: number) => void;
  clearLocation: () => void;
};

export const useUserLocation = (): UserLocationResponse => {
  const [ userLocation, setUserLocation ] = useState<UserLocation | null>(null);
  const [ isUserLocationLoading, setIsUserLocationLoading ] = useState(false);
  const [ userLocationError, setUserLocationError ] = useState<string | null>(null);
  const [ permissionDenied, setPermissionDenied ] = useState(false);

  const requestGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      setUserLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsUserLocationLoading(true);
    setUserLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: 'geolocation',
        });
        setIsUserLocationLoading(false);
        setPermissionDenied(false);
      },
      (err) => {
        setIsUserLocationLoading(false);
        setPermissionDenied(err.code === err.PERMISSION_DENIED);
        setUserLocationError(
          err.code === err.PERMISSION_DENIED
            ? 'Location access denied. Please enter your location manually.'
            : err.code === err.POSITION_UNAVAILABLE
            ? 'Location unavailable. Please enter your location manually.'
            : 'Location request timed out. Please try again or enter manually.',
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  }, []);

  const setManualLocation = useCallback((lat: number, lng: number) => {
    setUserLocation({ lat, lng, source: 'manual' });
    setUserLocationError(null);
  }, []);

  const clearLocation = useCallback(() => {
    setUserLocation(null);
    setUserLocationError(null);
  }, []);

  return {
    userLocation,
    isUserLocationLoading,
    userLocationError,
    permissionDenied,
    requestGeolocation,
    setManualLocation,
    clearLocation,
  };
};

